/*!
 * <ship-progress> : National Park Magic free-shipping progress bar ("Camper & Cabin")
 * A vintage pickup towing a canned-ham camper follows a straight park road,
 * passing pine trees on the way to a log cabin as the cart fills.
 *
 * Usage:
 *   <ship-progress threshold="7500" subtotal="2300" currency="USD"></ship-progress>
 *
 * Attributes
 *   threshold     (required) free-shipping threshold in minor units (cents). 7500 = $75.00
 *   subtotal      (required) current cart subtotal in minor units (cents)
 *   currency      ISO 4217 code for the message. Default "USD"
 *   locale        BCP 47 locale for number formatting. Default document lang or "en-US"
 *   truck-color   pickup body color. Default "#B4432F"
 *   camper-color  camper lower swoop color. Default "#4E6E5D"
 *
 * Methods
 *   el.setSubtotal(cents)  update and animate
 *   el.refresh()           fetch /cart.js (Shopify) and update from total_price
 *
 * Events
 *   "ship-progress:unlocked"  fired each time progress crosses into 100%
 *
 * No dependencies. Renders in Shadow DOM so theme CSS cannot alter it.
 * Fonts: load Oswald 500/600 in the document <head> (see README).
 */
(() => {
  if (customElements.get('ship-progress')) return;

  /* ---------- Exact design constants (do not change without design sign-off) ---------- */
  const GEOM = {
    viewW: 340,          // SVG viewBox width
    viewH: 68,           // SVG viewBox height
    x0: 20,              // road start x
    x1: 276,             // straight road end before the cabin
    baseY: 42,           // road centerline baseline y
    samples: 240,        // polyline resolution
    vehicleScale: 0.26,  // truck + camper artwork scale
    tilt: 0.75,          // vehicle rotation = road heading x 0.75
    truckAnchor: [60, 101],   // truck artwork point placed on the road (horizontal center, wheel bottom)
    camperAnchor: [75, 101],  // camper artwork point placed on the road
    // Distance along the road between truck anchor and camper anchor, in artwork units:
    // truck rear (56 behind its anchor) + camper front (71 ahead of its anchor) + 9 hitch gap.
    hitchArtwork: 56 + 71 + 9
  };
  GEOM.hitch = GEOM.hitchArtwork * GEOM.vehicleScale; // 35.36 viewBox units

  const C = {
    panel:      '#F3E7CF',
    mountains:  '#E6D5B3',
    roadShadow: '#1F3A4D', // at 0.18 opacity
    road:       '#D2BF99',
    traveled:   '#4E6E5D',
    centerLine: '#F3E7CF', // at 0.9 opacity
    navy:       '#1F3A4D',
    window:     '#2A4A5E',
    cream:      '#F3E7CF',
    mustard:    '#D9A441',
    rust:       '#B4432F',
    log:        '#8A5A3B',
    logLine:    '#5E3B24',
    logEnd:     '#C9A27A',
    chimney:    '#6B5A45',
    smoke:      '#D2BF99',
    pine:       '#2F5D46',
    trunk:      '#6B5A45',
    doneText:   '#2F5D46',
    text:       '#1F3A4D'
  };

  const ANIM_MS = 600;

  /* ---------- Road geometry: straight polyline, measured for arc length ---------- */
  function buildRoad() {
    const { x0, x1, baseY, samples } = GEOM;
    const pts = [];
    for (let i = 0; i <= samples; i++) {
      const x = x0 + (x1 - x0) * i / samples;
      const y = baseY;
      pts.push([x, y]);
    }
    const cum = [0];
    for (let i = 1; i <= samples; i++) {
      cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    }
    const d = 'M' + pts.map(p => p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' L');
    return { pts, cum, total: cum[samples], d };
  }
  const ROAD = buildRoad();

  // Point + heading (degrees) at arc length s (viewBox units) along the road.
  function pointAtLength(s) {
    const { pts, cum, total } = ROAD;
    const n = pts.length - 1;
    const target = Math.min(total, Math.max(0, s));
    let i = 1;
    while (i < n && cum[i] < target) i++;
    const a = pts[i - 1], b = pts[i];
    const seg = (cum[i] - cum[i - 1]) || 1;
    const t = Math.min(1, Math.max(0, (target - cum[i - 1]) / seg));
    return {
      x: a[0] + (b[0] - a[0]) * t,
      y: a[1] + (b[1] - a[1]) * t,
      ang: Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI
    };
  }

  function transformFor(p, ang, anchor) {
    return `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)}) rotate(${(ang * GEOM.tilt).toFixed(2)}) ` +
           `scale(${GEOM.vehicleScale}) translate(${-anchor[0]} ${-anchor[1]})`;
  }

  const chord = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // Binary search for the arc length s in [lo, hi] where chord(pointAtLength(s), ref) == dist.
  // `forward` = true when chord grows with s.
  function solveChord(ref, dist, lo, hi, forward) {
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      const c = chord(pointAtLength(mid), ref);
      if ((c < dist) === forward) lo = mid; else hi = mid;
    }
    return (lo + hi) / 2;
  }

  // Truck arc length at 0%: the spot whose straight-line distance from the road start equals the hitch.
  const S_TRUCK_0 = solveChord(pointAtLength(0), GEOM.hitch, 0, GEOM.hitch * 2, true);

  // For progress f (0..1): truck runs from S_TRUCK_0 (camper parked at road start) to the road end.
  // The camper sits on the road a fixed straight-line distance (`hitch`) behind the truck and is
  // rotated to point at the truck, like a real towed trailer.
  function layout(f) {
    const { total } = ROAD;
    const sTruck = S_TRUCK_0 + f * (total - S_TRUCK_0);
    const pTruck = pointAtLength(sTruck);
    const sCamper = solveChord(pTruck, GEOM.hitch, Math.max(0, sTruck - GEOM.hitch * 2), sTruck, false);
    const pCamper = pointAtLength(sCamper);
    const camperAng = Math.atan2(pTruck.y - pCamper.y, pTruck.x - pCamper.x) * 180 / Math.PI;
    return {
      truck: transformFor(pTruck, pTruck.ang, GEOM.truckAnchor),
      camper: transformFor(pCamper, camperAng, GEOM.camperAnchor),
      fillPct: sTruck / total * 100
    };
  }

  /* ---------- Artwork (artwork coordinates; vehicles face right; wheel bottoms at y 101) ---------- */
  const CAMPER = `
    <path d="M146 76 L156 76" stroke="${C.navy}" stroke-width="6" stroke-linecap="round"/>
    <path d="M6 84 L6 46 Q6 14 46 12 L104 12 Q140 14 146 50 L146 84 Z" fill="${C.cream}"/>
    <path class="cc" d="M6 84 L6 62 Q76 48 146 66 L146 84 Z"/>
    <path d="M6 84 L6 46 Q6 14 46 12 L104 12 Q140 14 146 50 L146 84 Z" fill="none" stroke="${C.navy}" stroke-width="6" stroke-linejoin="round"/>
    <rect x="98" y="24" width="32" height="20" rx="7" fill="${C.window}" stroke="${C.navy}" stroke-width="4"/>
    <rect x="28" y="28" width="24" height="50" rx="4" fill="${C.cream}" stroke="${C.navy}" stroke-width="4"/>
    <circle cx="40" cy="41" r="6" fill="${C.window}"/>
    <circle cx="78" cy="88" r="13" fill="${C.navy}"/>
    <circle cx="78" cy="88" r="5" fill="${C.cream}"/>`;

  const TRUCK = `
    <path d="M-5 75 L6 75" stroke="${C.navy}" stroke-width="6" stroke-linecap="round"/>
    <rect class="tc" x="4" y="46" width="52" height="32" rx="3" stroke="${C.navy}" stroke-width="6"/>
    <path class="tc" d="M54 78 L54 30 Q54 20 64 20 L80 20 Q90 20 94 32 L98 46 L112 48 Q120 50 120 60 L120 74 Q120 78 116 78 Z" stroke="${C.navy}" stroke-width="6" stroke-linejoin="round"/>
    <path d="M61 28 Q61 26 64 26 L79 26 Q85 26 87 32 L90 42 L61 42 Z" fill="${C.window}"/>
    <rect x="6" y="58" width="112" height="5" fill="${C.cream}"/>
    <circle cx="115" cy="55" r="4.5" fill="${C.cream}" stroke="${C.navy}" stroke-width="2.5"/>
    <rect x="114" y="74" width="10" height="5" rx="2" fill="${C.navy}"/>
    <circle cx="26" cy="88" r="13" fill="${C.navy}"/>
    <circle cx="26" cy="88" r="5" fill="${C.cream}"/>
    <circle cx="96" cy="88" r="13" fill="${C.navy}"/>
    <circle cx="96" cy="88" r="5" fill="${C.cream}"/>`;

  // Log cabin: drawn directly in viewBox coordinates at the right end of the road.
  const logEnds = [39.4, 44.1, 48.9, 53.6]
    .map(y => [302, 336].map(x =>
      `<circle cx="${x}" cy="${y}" r="2.2" fill="${C.logEnd}" stroke="${C.navy}" stroke-width="0.8"/>`).join(''))
    .join('');
  const CABIN = `
    <circle cx="333" cy="20" r="2.6" fill="${C.smoke}"/>
    <circle cx="336" cy="14" r="3.2" fill="${C.smoke}" fill-opacity="0.8"/>
    <circle cx="333" cy="7" r="3.8" fill="${C.smoke}" fill-opacity="0.55"/>
    <rect x="329" y="22" width="5" height="11" fill="${C.chimney}" stroke="${C.navy}" stroke-width="1"/>
    <rect x="302" y="37" width="34" height="19" fill="${C.log}" stroke="${C.navy}" stroke-width="1.3"/>
    <path d="M302 41.75 H336 M302 46.5 H336 M302 51.25 H336" stroke="${C.logLine}" stroke-width="1"/>
    ${logEnds}
    <path d="M297 38.5 L319 23 L341 38.5 Z" fill="${C.rust}" stroke="${C.navy}" stroke-width="1.3" stroke-linejoin="round"/>
    <rect x="315.5" y="45" width="7" height="11" rx="1" fill="${C.logLine}" stroke="${C.navy}" stroke-width="0.8"/>
    <rect x="306" y="42.5" width="6.5" height="5.5" fill="${C.mustard}" stroke="${C.navy}" stroke-width="0.8"/>
    <rect x="325.5" y="42.5" width="6.5" height="5.5" fill="${C.mustard}" stroke="${C.navy}" stroke-width="0.8"/>`;

  const PINE = `
    <path d="M0 -26 L8 -13 L4 -13 L9 -4 L-9 -4 L-4 -13 L-8 -13 Z" fill="${C.pine}"/>
    <rect x="-1.5" y="-4" width="3" height="6" fill="${C.trunk}"/>`;
  const PINES = [
    [82, 34, 0.70],
    [156, 30, 0.84],
    [224, 35, 0.66]
  ].map(([x, y, scale]) =>
    `<g class="pine" transform="translate(${x} ${y}) scale(${scale})">${PINE}</g>`
  ).join('');

  const TEMPLATE = `
    <style>
      :host { display: block; --sp-truck: #B4432F; --sp-camper: #4E6E5D; }
      .wrap {
        background: ${C.panel};
        padding: 18px 20px 20px;
        display: flex; flex-direction: column; gap: 14px;
        box-sizing: border-box;
      }
      :host([hide-message]) .msg { display: none; }
      :host([compact]) .wrap { padding: 0 20px 2px; gap: 0; }
      :host([compact]) .art { width: min(100%, 300px); align-self: center; }
      .msg {
        margin: 0;
        font-family: 'Oswald', 'Arial Narrow', sans-serif;
        font-weight: 500; font-size: 16px; line-height: 1.3; letter-spacing: 0.5px;
        color: ${C.text};
      }
      .msg.done { font-weight: 600; color: ${C.doneText}; }
      .art { width: 100%; max-width: 340px; aspect-ratio: 340 / 68; }
      svg { display: block; width: 100%; height: 100%; overflow: visible; }
      .tc { fill: var(--sp-truck); }
      .cc { fill: var(--sp-camper); }
    </style>
    <div class="wrap">
      <p class="msg" aria-live="polite"></p>
      <div class="art" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-label="Progress toward free shipping">
        <svg viewBox="0 0 ${GEOM.viewW} ${GEOM.viewH}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M0 68 L34 24 L62 36 L104 4 L140 32 L182 10 L222 36 L262 6 L300 28 L340 16 L340 68 Z" fill="${C.mountains}"/>
          <g class="pine-grove">${PINES}</g>
          <path d="${ROAD.d}" pathLength="100" fill="none" stroke="${C.roadShadow}" stroke-opacity="0.18" stroke-width="15" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="${ROAD.d}" pathLength="100" fill="none" stroke="${C.road}" stroke-width="12" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="fill" d="${ROAD.d}" pathLength="100" fill="none" stroke="${C.traveled}" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="0 100"/>
          <path d="${ROAD.d}" pathLength="100" fill="none" stroke="${C.centerLine}" stroke-opacity="0.9" stroke-width="1.6" stroke-dasharray="1.6 1.4"/>
          <g class="cabin" transform="translate(-18 -8)">${CABIN}</g>
          <g class="camper">${CAMPER}</g>
          <g class="truck">${TRUCK}</g>
        </svg>
      </div>
    </div>`;

  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
  const num = v => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : 0; };

  class ShipProgress extends HTMLElement {
    static get observedAttributes() {
      return ['threshold', 'subtotal', 'currency', 'locale', 'truck-color', 'camper-color'];
    }

    constructor() {
      super();
      this._f = null;
      this._raf = 0;
      this._wasDone = null;
    }

    connectedCallback() {
      if (!this.shadowRoot) {
        const root = this.attachShadow({ mode: 'open' });
        root.innerHTML = TEMPLATE;
        this._msg = root.querySelector('.msg');
        this._bar = root.querySelector('.art');
        this._fill = root.querySelector('.fill');
        this._truck = root.querySelector('.truck');
        this._camper = root.querySelector('.camper');
      }
      this._update(false);
    }

    disconnectedCallback() { cancelAnimationFrame(this._raf); }

    attributeChangedCallback() { if (this.shadowRoot) this._update(true); }

    setSubtotal(cents) { this.setAttribute('subtotal', String(Math.round(cents))); }

    async refresh() {
      const root = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';
      const res = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.setSubtotal(cart.total_price);
      return cart;
    }

    _format(cents) {
      const currency = this.getAttribute('currency') || 'USD';
      const locale = this.getAttribute('locale') || document.documentElement.lang || 'en-US';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    _update(animate) {
      const threshold = num(this.getAttribute('threshold'));
      const subtotal = Math.max(0, num(this.getAttribute('subtotal')));
      const target = threshold > 0 ? Math.min(1, subtotal / threshold) : 1;
      const done = target >= 1;

      this.style.setProperty('--sp-truck', this.getAttribute('truck-color') || '#B4432F');
      this.style.setProperty('--sp-camper', this.getAttribute('camper-color') || '#4E6E5D');

      if (done) {
        this._msg.textContent = "You've unlocked free shipping!";
        this._msg.classList.add('done');
      } else {
        this._msg.textContent = `You're ${this._format(threshold - subtotal)} away from free shipping`;
        this._msg.classList.remove('done');
      }

      const pct = Math.round(target * 100);
      this._bar.setAttribute('aria-valuenow', String(pct));
      this._bar.setAttribute('aria-valuetext', done ? 'Free shipping unlocked' : `${pct}% of the way to free shipping`);

      if (done && this._wasDone === false) {
        this.dispatchEvent(new CustomEvent('ship-progress:unlocked', { bubbles: true, composed: true }));
      }
      this._wasDone = done;

      cancelAnimationFrame(this._raf);
      const from = this._f;
      if (!animate || from === null || reducedMotion() || from === target) {
        this._draw(target);
        return;
      }
      const start = performance.now();
      const step = now => {
        const t = Math.min(1, (now - start) / ANIM_MS);
        this._draw(from + (target - from) * easeOutCubic(t));
        if (t < 1) this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
    }

    _draw(f) {
      this._f = f;
      const ff = Math.max(0, Math.min(1, f));
      const L = layout(ff);
      // Green fill runs from the road start to the truck. Hidden at 0% (rig is parked, nothing traveled).
      this._fill.setAttribute('stroke-dasharray', `${L.fillPct.toFixed(3)} 100`);
      this._fill.style.visibility = ff < 0.005 ? 'hidden' : 'visible';
      this._truck.setAttribute('transform', L.truck);
      this._camper.setAttribute('transform', L.camper);
    }
  }

  customElements.define('ship-progress', ShipProgress);
})();
